import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, Lock } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import PeriodFilter, { applyPeriodFilter } from '../components/PeriodFilter';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#84cc16', '#6366f1'];

const formatCR = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');

export default function DetailFluxInvestisseur({ user, onLogout }) {
  const { type } = useParams(); // 'encaissements' | 'decaissements'
  const navigate = useNavigate();
  const isEnc = type === 'encaissements';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  const palette = isEnc
    ? { name: 'Encaissements', color: 'green', Icon: TrendingUp, headBg: 'bg-green-600', cellBg: 'bg-green-50', cellBorder: 'border-green-200', textHead: 'text-green-800', textCell: 'text-green-700' }
    : { name: 'Décaissements', color: 'red', Icon: TrendingDown, headBg: 'bg-red-600', cellBg: 'bg-red-50', cellBorder: 'border-red-200', textHead: 'text-red-800', textCell: 'text-red-700' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`);
      const list = isEnc ? (res.data.credits || []) : (res.data.debits || []);
      setRows(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isEnc]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => applyPeriodFilter(rows, period), [rows, period]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const key = (isEnc ? (r.annonceur || r.categorie) : r.categorie) || 'Autres';
      map[key] = (map[key] || 0) + (parseFloat(r.montant) || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered, isEnc]);

  const total = filtered.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid={`detail-${type}`}>
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate(-1)} variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <palette.Icon className={`h-7 w-7 text-${palette.color}-600`} />
                Détail des {palette.name}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {filtered.length} mouvement{filtered.length > 1 ? 's' : ''} —{' '}
                <strong>{formatCR(total)} €</strong>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PeriodFilter value={period} onChange={setPeriod} color={palette.color === 'red' ? 'red' : 'green'} testIdPrefix={`flux-${type}-period`} />
            <Badge variant="outline" className="gap-1 border-gray-300 text-gray-600 bg-gray-50">
              <Lock className="h-3 w-3" /> Lecture seule
            </Badge>
          </div>
        </div>

        {/* Graphique + Tableau */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-lg">
            <CardHeader className={`${palette.headBg} text-white`}>
              <CardTitle>Répartition par catégorie</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {grouped.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-400 italic">
                  Aucune donnée à afficher pour cette période
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={grouped}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={110}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {grouped.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${formatCR(v)} €`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className={`${palette.headBg} text-white`}>
              <CardTitle>Détail des mouvements</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-y-auto max-h-[400px]">
                <table className="w-full text-sm">
                  <thead className={`${palette.cellBg} border-b ${palette.cellBorder} sticky top-0`}>
                    <tr>
                      <th className={`px-4 py-2 text-left text-xs font-semibold ${palette.textHead} uppercase`}>Date</th>
                      <th className={`px-4 py-2 text-left text-xs font-semibold ${palette.textHead} uppercase`}>
                        {isEnc ? 'Annonceur' : 'Catégorie'}
                      </th>
                      <th className={`px-4 py-2 text-right text-xs font-semibold ${palette.textHead} uppercase`}>Montant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-4 text-center text-gray-400 italic">
                          Aucun mouvement
                        </td>
                      </tr>
                    ) : (
                      filtered
                        .slice()
                        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                        .map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-700">{r.date || '—'}</td>
                            <td className="px-4 py-2 text-gray-800">
                              {isEnc ? (r.annonceur || r.categorie || '—') : (r.categorie || '—')}
                              {r.description && <p className="text-xs text-gray-500">{r.description}</p>}
                            </td>
                            <td className={`px-4 py-2 text-right font-medium ${palette.textCell}`}>
                              {formatCR(r.montant)} €
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                  <tfoot className={`${palette.cellBg} border-t-2 ${palette.cellBorder}`}>
                    <tr>
                      <td colSpan={2} className={`px-4 py-3 font-bold ${palette.textHead} uppercase`}>Total</td>
                      <td className={`px-4 py-3 text-right font-bold ${palette.textCell} text-lg`}>{formatCR(total)} €</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
